import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
} from "reactstrap";
import { Link } from "react-router-dom";

// Charts
import StackedColumnChart from "./StackedColumnChart";
import ApexRadial from "./ApexRadial";

// Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

import { withTranslation } from "react-i18next";
import {
  getAllreservation,
  getAllvisas,
  getallhotelreservation,
  getcounte,
  getcounte2,
  getcounte3,
  getmonthly,
  countpayement,
} from "helpers/requests";

const formatMoney = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString("fr-DZ") + " DA"
    : "0 DA";

const Dashboard = (props) => {
  const username = localStorage.getItem("username") || "Admin";
  const role = localStorage.getItem("role") || "Administrator";

  const [counts, setCounts] = useState({
    properties: 0,
    users: 0,
    visas: 0,
    hotelReservations: 0,
    voyageReservations: 0,
    payments: 0,
  });

  const [datamonth, setDatamonth] = useState([]);
  const [currentMonthData, setCurrentMonthData] = useState(null);

  const [reservations, setReservations] = useState([]);
  const [hotelReservations, setHotelReservations] = useState([]);
  const [visas, setVisas] = useState([]);

  const [monthlyEarnings, setMonthlyEarnings] = useState(0);
  const [currentMonthPercentage, setCurrentMonthPercentage] = useState(0);
  const [reservationEarningc, setReservationEarning] = useState(0);
  const [visasearning, setVisasEarning] = useState(0);

  // ── Helpers ─────────────────────────────────────────────────────
  const calculateAcceptancePercentage = (reservations, visas) => {
    const currentMonth = new Date().getMonth();
    let totalEntries = 0;
    let totalAccepted = 0;

    reservations.forEach((res) => {
      const month = new Date(res.createdAt).getMonth();
      if (month === currentMonth) {
        totalEntries++;
        if (res.status === "ACCEPTED") totalAccepted++;
      }
    });
    visas.forEach((visa) => {
      const month = new Date(visa.createdAt).getMonth();
      if (month === currentMonth) {
        totalEntries++;
        if (visa.status === "ACCEPTED") totalAccepted++;
      }
    });
    return totalEntries > 0
      ? ((totalAccepted / totalEntries) * 100).toFixed(2)
      : 0;
  };

  const calculateMonthlyEarnings = (reservations, visas) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const reservationEarnings = reservations
      .filter(
        (res) =>
          res.status === "ACCEPTED" &&
          new Date(res.createdAt).getMonth() === currentMonth &&
          new Date(res.createdAt).getFullYear() === currentYear
      )
      .reduce((sum, res) => sum + parseFloat(res.totalAmount || 0), 0);

    const visaEarnings = visas
      .filter(
        (visa) =>
          visa.status === "ACCEPTED" &&
          new Date(visa.createdAt).getMonth() === currentMonth &&
          new Date(visa.createdAt).getFullYear() === currentYear
      )
      .reduce((sum, visa) => sum + parseFloat(visa.totalPrice || 0), 0);

    setReservationEarning(reservationEarnings);
    setVisasEarning(visaEarnings);
    return reservationEarnings + visaEarnings;
  };

  // ── Data fetch ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [
          properties,
          users,
          visasCount,
          paymentsCount,
          monthly,
          reservationData,
          visaData,
          hotelData,
        ] = await Promise.all([
          getcounte().catch(() => 0),
          getcounte2().catch(() => 0),
          getcounte3().catch(() => 0),
          countpayement().catch(() => 0),
          getmonthly().catch(() => []),
          getAllreservation().catch(() => []),
          getAllvisas().catch(() => []),
          getallhotelreservation().catch(() => []),
        ]);

        setCounts({
          properties: Number(properties) || 0,
          users: Number(users) || 0,
          visas: Number(visasCount) || 0,
          hotelReservations: Array.isArray(hotelData) ? hotelData.length : 0,
          voyageReservations: Array.isArray(reservationData) ? reservationData.length : 0,
          payments: Number(paymentsCount) || 0,
        });

        setDatamonth(monthly || []);
        setReservations(reservationData || []);
        setVisas(visaData || []);
        setHotelReservations(hotelData || []);

        const currentMonthKey = new Date().toISOString().slice(0, 7);
        const currentData = (monthly || []).find((d) => d.month === currentMonthKey);
        setCurrentMonthData(currentData);

        const totalEarnings = calculateMonthlyEarnings(reservationData || [], visaData || []);
        setMonthlyEarnings(totalEarnings);

        const percentages = calculateAcceptancePercentage(reservationData || [], visaData || []);
        setCurrentMonthPercentage(percentages);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    })();
  }, []);

  document.title = "Tableau de bord";

  // KPI tiles config
  const kpis = [
    {
      title: "Propriétés",
      value: counts.properties,
      icon: "bx-buildings",
      color: "primary",
      link: "/proprety",
    },
    {
      title: "Utilisateurs",
      value: counts.users,
      icon: "bx-user",
      color: "info",
      link: "/users",
    },
    {
      title: "Demandes de visa",
      value: counts.visas,
      icon: "bxl-visa",
      color: "warning",
      link: "/visa",
    },
    {
      title: "Réservations hôtel",
      value: counts.hotelReservations,
      icon: "bx-bed",
      color: "success",
      link: "/hotel",
    },
    {
      title: "Voyages organisés",
      value: counts.voyageReservations,
      icon: "bx-plane-take-off",
      color: "danger",
      link: "/voyage",
    },
    {
      title: "Paiements",
      value: counts.payments,
      icon: "bx-money",
      color: "dark",
      link: "/payments",
    },
  ];

  const today = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const totalThisMonth = currentMonthData?.total || 0;
  const voyageThisMonth = currentMonthData?.reservation || 0;
  const hotelThisMonth = currentMonthData?.hotelReservation || 0;
  const visaThisMonth = currentMonthData?.visa || 0;

  // Recent activity = up to 5 most recent items mixed across the three sources
  const recents = React.useMemo(() => {
    const all = [
      ...hotelReservations.map((r) => ({
        kind: "Hôtel",
        color: "success",
        icon: "bx-bed",
        label: `${r.name || ""} ${r.surnom || ""}`.trim() || "—",
        meta: `${r.totalPrice ? formatMoney(r.totalPrice) : "—"} · ${r.status || "—"}`,
        date: r.createdAt,
        href: "/hotel",
      })),
      ...reservations.map((r) => ({
        kind: "Voyage",
        color: "danger",
        icon: "bx-plane-take-off",
        label: `${r.nom || ""} ${r.surnom || ""}`.trim() || "—",
        meta: `${r.totalAmount ? formatMoney(r.totalAmount) : "—"} · ${r.status || "—"}`,
        date: r.createdAt,
        href: "/voyage",
      })),
      ...visas.map((v) => ({
        kind: "Visa",
        color: "warning",
        icon: "bxl-visa",
        label: `${v.applicantName || ""} ${v.applicantSurname || ""}`.trim() || "—",
        meta: `${v.country || "—"} · ${v.status || "—"}`,
        date: v.createdAt,
        href: "/visa",
      })),
    ];
    return all
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 8);
  }, [hotelReservations, reservations, visas]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Accueil")}
            breadcrumbItem={props.t("Tableau de bord")}
          />

          {/* ── Hero ─────────────────────────────────────────── */}
          <Card className="overflow-hidden mb-4">
            <div
              style={{
                background:
                  "linear-gradient(135deg, #043263 0%, #239efe 100%)",
                color: "#fff",
                padding: "28px 32px",
              }}
            >
              <Row className="align-items-center">
                <Col md={7}>
                  <small style={{ opacity: 0.85, letterSpacing: 0.5 }}>
                    {today}
                  </small>
                  <h2 className="text-white mt-1 mb-1">
                    Bonjour, {username} 👋
                  </h2>
                  <p className="mb-0" style={{ opacity: 0.92 }}>
                    Voici un aperçu de l'activité de ProTour aujourd'hui.
                    Suivez les réservations, paiements et demandes en temps réel.
                  </p>
                </Col>
                <Col md={5} className="text-md-end mt-3 mt-md-0">
                  <div
                    className="d-inline-block px-4 py-3 rounded-3"
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <small style={{ opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Revenus du mois
                    </small>
                    <h3 className="text-white mb-0 mt-1">
                      {formatMoney(totalThisMonth || monthlyEarnings)}
                    </h3>
                    <small className="text-white" style={{ opacity: 0.85 }}>
                      <i className="mdi mdi-account-check me-1" />
                      {currentMonthPercentage}% acceptés ce mois
                    </small>
                  </div>
                </Col>
              </Row>
            </div>
          </Card>

          {/* ── KPI tiles ────────────────────────────────────── */}
          <Row>
            {kpis.map((kpi, i) => (
              <Col xl={2} lg={4} md={6} key={i}>
                <Card className="mini-stats-wid h-100">
                  <CardBody>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-2 font-size-12 text-uppercase">
                          {kpi.title}
                        </p>
                        <h4 className="mb-0">{kpi.value}</h4>
                      </div>
                      <div className={`avatar-sm rounded-circle bg-${kpi.color} align-self-center mini-stat-icon`}>
                        <span className={`avatar-title rounded-circle bg-${kpi.color}`}>
                          <i className={`bx ${kpi.icon} font-size-20 text-white`} />
                        </span>
                      </div>
                    </div>
                    <Link
                      to={kpi.link}
                      className={`text-${kpi.color} font-size-11 mt-3 d-inline-block`}
                    >
                      Voir tous <i className="mdi mdi-arrow-right" />
                    </Link>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── Earnings + Acceptance ────────────────────────── */}
          <Row>
            <Col xl={8}>
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap align-items-center mb-3">
                    <h4 className="card-title mb-0">
                      <i className="mdi mdi-chart-bar text-primary me-2" />
                      Revenus mensuels
                    </h4>
                    <Badge color="primary" pill className="ms-2">
                      Année en cours
                    </Badge>
                  </div>
                  <p className="text-muted mb-4">
                    Répartition des revenus par catégorie sur l'année.
                  </p>
                  <StackedColumnChart
                    periodData={[]}
                    dataColors='["--bs-success", "--bs-primary", "--bs-warning"]'
                    datamonth={datamonth}
                  />
                </CardBody>
              </Card>
            </Col>

            <Col xl={4}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3">
                    <i className="mdi mdi-trophy-outline text-primary me-2" />
                    Performance du mois
                  </CardTitle>

                  <div className="text-center my-3">
                    <ApexRadial
                      currentMonthPercentage={currentMonthPercentage}
                      dataColors='["--bs-success"]'
                    />
                    <small className="text-muted d-block">
                      Taux d'acceptation des demandes (réservations + visas)
                    </small>
                  </div>

                  <div className="border-top pt-3">
                    <p className="text-muted font-size-12 text-uppercase mb-3">
                      Détail du mois
                    </p>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>
                        <i className="bx bx-plane-take-off text-danger me-2" />
                        Voyages
                      </span>
                      <strong>{formatMoney(voyageThisMonth || reservationEarningc)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>
                        <i className="bx bx-bed text-success me-2" />
                        Hôtels
                      </span>
                      <strong>{formatMoney(hotelThisMonth)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span>
                        <i className="bx bxl-visa text-warning me-2" />
                        Visas
                      </span>
                      <strong>{formatMoney(visaThisMonth || visasearning)}</strong>
                    </div>
                    <div className="border-top pt-3 d-flex justify-content-between align-items-center">
                      <strong>Total</strong>
                      <h5 className="text-primary mb-0">
                        {formatMoney(totalThisMonth || monthlyEarnings)}
                      </h5>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ── Recent activity ─────────────────────────────── */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <CardTitle className="mb-0">
                      <i className="mdi mdi-clock-outline text-primary me-2" />
                      Activité récente
                    </CardTitle>
                    <small className="text-muted">
                      {recents.length} dernières demandes
                    </small>
                  </div>

                  {recents.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <i className="mdi mdi-inbox-outline display-6 d-block mb-2" />
                      Aucune activité pour le moment.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Type</th>
                            <th>Client</th>
                            <th>Détails</th>
                            <th>Date</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recents.map((r, i) => (
                            <tr key={i}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className={`avatar-xs me-2`}>
                                    <span className={`avatar-title rounded-circle bg-${r.color}-subtle text-${r.color}`}>
                                      <i className={`bx ${r.icon}`} />
                                    </span>
                                  </div>
                                  <Badge color={r.color} className="font-size-10">
                                    {r.kind}
                                  </Badge>
                                </div>
                              </td>
                              <td>
                                <strong>{r.label}</strong>
                              </td>
                              <td>
                                <small className="text-muted">{r.meta}</small>
                              </td>
                              <td>
                                <small>
                                  {r.date
                                    ? new Date(r.date).toLocaleDateString("fr-FR", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })
                                    : "—"}
                                </small>
                              </td>
                              <td className="text-end">
                                <Link to={r.href} className="text-primary">
                                  <i className="mdi mdi-arrow-right font-size-18" />
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ── Account info bar ────────────────────────────── */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <div className="avatar-md me-3">
                          <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-22">
                            <i className="mdi mdi-account-tie" />
                          </span>
                        </div>
                        <div>
                          <h5 className="mb-1">{username}</h5>
                          <small className="text-muted">
                            <Badge color="primary-subtle" className="text-primary">
                              {role}
                            </Badge>
                          </small>
                        </div>
                      </div>
                    </Col>
                    <Col md={6} className="text-md-end mt-3 mt-md-0">
                      <Link to="/proprety" className="btn btn-light me-2">
                        <i className="bx bx-buildings me-1" /> Propriétés
                      </Link>
                      <Link to="/payments" className="btn btn-primary">
                        <i className="bx bx-money me-1" /> Paiements
                      </Link>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Dashboard);
